import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import axiosInstance from '../../../api/axiosConfig';
import PageContainer from '../../common/PageContainer';
import PageHeader from '../../common/PageHeader';
import EmptyState from '../../common/EmptyState';
import { STORAGE_KEYS } from '../../../constants';

const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
const maxImageSize = 5 * 1024 * 1024;

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatPercent = (value) => {
  if (!Number.isFinite(Number(value))) return 'N/A';
  return `${Number(value).toFixed(1)}%`;
};

const ViewStudent = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [examResults, setExamResults] = useState([]);
  const [loadingExamResults, setLoadingExamResults] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [openResetConfirmDialog, setOpenResetConfirmDialog] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const currentUser = useMemo(
    () => ({
      userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
      schoolId: localStorage.getItem(STORAGE_KEYS.SCHOOL_ID),
      role: localStorage.getItem(STORAGE_KEYS.USER_ROLE),
    }),
    []
  );

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleError = useCallback(
    (error, fallbackMessage) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : '') ||
        fallbackMessage;
      showSnackbar(message, 'error');
    },
    [showSnackbar]
  );

  const fetchStudent = useCallback(async () => {
    if (!studentId) {
      setLoading(false);
      showSnackbar('Student ID is missing in URL.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/students/${studentId}`);
      setStudent(response.data);
    } catch (error) {
      setStudent(null);
      handleError(error, 'Failed to fetch student details.');
    } finally {
      setLoading(false);
      setLastUpdatedAt(new Date());
    }
  }, [studentId, handleError, showSnackbar]);

  useEffect(() => {
    fetchStudent();
  }, [fetchStudent]);

  const fetchAchievements = useCallback(async () => {
    if (!studentId) return;
    setLoadingAchievements(true);
    try {
      const response = await axiosInstance.get(`/api/achievements/students/${studentId}`);
      setAchievements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setAchievements([]);
      handleError(error, 'Failed to load student achievements.');
    } finally {
      setLoadingAchievements(false);
    }
  }, [handleError, studentId]);

  const fetchExamResults = useCallback(async () => {
    const schoolId = currentUser.schoolId;
    if (!studentId || !schoolId) return;
    setLoadingExamResults(true);
    try {
      const response = await axiosInstance.get('/api/students/exam-results', {
        params: { studentId },
        headers: { schoolId },
      });
      setExamResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setExamResults([]);
      handleError(error, 'Failed to load exam results.');
    } finally {
      setLoadingExamResults(false);
    }
  }, [currentUser.schoolId, handleError, studentId]);

  useEffect(() => {
    fetchAchievements();
    fetchExamResults();
  }, [fetchAchievements, fetchExamResults]);

  const primaryResult = examResults?.[0] || null;
  const subjectResults = useMemo(
    () => (Array.isArray(primaryResult?.subjectResults) ? primaryResult.subjectResults : []),
    [primaryResult]
  );
  const subjectChartData = subjectResults.map((subject) => {
    const computedPercentage =
      Number.isFinite(subject?.percentage) ? subject.percentage :
      (Number.isFinite(subject?.marksObtained) && Number.isFinite(subject?.maxMarks) && subject.maxMarks > 0
        ? (subject.marksObtained / subject.maxMarks) * 100
        : null);
    return {
      name: subject?.subjectName || 'Subject',
      percentage: Number.isFinite(computedPercentage) ? Number(computedPercentage.toFixed(1)) : 0,
    };
  });

  const attendancePresent = student?.attendanceSummary?.daysPresent;
  const attendanceAbsent = student?.attendanceSummary?.daysAbsent;
  const attendancePercentage = Number(
    student?.attendancePercentage ??
    student?.attendanceSummary?.percentage ??
    NaN
  );
  const academicAverage = subjectChartData.length
    ? Number((subjectChartData.reduce((sum, row) => sum + row.percentage, 0) / subjectChartData.length).toFixed(1))
    : null;
  const participationPercentage = Number(
    student?.participationRate ??
    student?.participationScore ??
    NaN
  );
  const supportSystemsSummary = [student?.hasTransportation ? 'Transport' : null, student?.hasHostel ? 'Hostel' : null]
    .filter(Boolean)
    .join(' + ');
  const refreshedAtLabel = lastUpdatedAt
    ? lastUpdatedAt.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'N/A';
  const latestExamLabel =
    primaryResult?.examName ||
    primaryResult?.examTypeName ||
    primaryResult?.examinationTypeName ||
    'Latest published exam';
  const quickStats = [
    { label: 'Attendance', value: formatPercent(attendancePercentage), helper: `${attendancePresent || 0} present · ${attendanceAbsent || 0} absent` },
    { label: 'Academic Avg', value: academicAverage !== null ? formatPercent(academicAverage) : 'N/A', helper: latestExamLabel },
    { label: 'Achievements', value: `${achievements.length}`, helper: achievements.length > 0 ? 'Recorded awards and certificates' : 'No achievements yet' },
    { label: 'Participation', value: formatPercent(participationPercentage), helper: supportSystemsSummary || 'No support systems mapped' },
  ];
  const basicRows = [
    ['Admission No', student?.admissionNumber],
    ['Roll No', student?.rollNumber],
    ['Class', student?.classes?.className],
    ['Section', student?.section?.sectionName],
    ['Academic Year', student?.academicYear],
    ['Admission Date', formatDate(student?.admissionDate)],
  ];
  const contactRows = [
    ['Parent Name', student?.parentName],
    ['Parent Phone', student?.parentPhone],
    ['Student Phone', student?.phone],
    ['Email', student?.email],
    ['Address', student?.address],
  ];
  const supportRows = [
    ['Status', student?.status || 'N/A'],
    ['Gender', student?.gender || 'N/A'],
    ['Age', student?.age || 'N/A'],
    ['Transport', student?.hasTransportation ? 'Enabled' : 'Not enabled'],
    ['Hostel', student?.hasHostel ? 'Enabled' : 'Not enabled'],
  ];

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!allowedImageTypes.includes(file.type)) {
      showSnackbar('Please upload JPG, PNG, or GIF image.', 'warning');
      return;
    }
    if (file.size > maxImageSize) {
      showSnackbar('Image must be smaller than 5MB.', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('studentId', studentId);

    setUploading(true);
    try {
      await axiosInstance.post('/api/images/profile/student/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSnackbar('Profile image updated.');
      fetchStudent();
    } catch (error) {
      handleError(error, 'Failed to upload student image.');
    } finally {
      setUploading(false);
    }
  };

  const handleClearImage = async () => {
    setUploading(true);
    try {
      await axiosInstance.delete(`/api/images/profile/student/clear/${studentId}`);
      showSnackbar('Profile image removed.');
      fetchStudent();
    } catch (error) {
      handleError(error, 'Failed to remove profile image.');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmResetPassword = async () => {
    setOpenResetConfirmDialog(false);
    if (!currentUser.userId || !currentUser.schoolId || !currentUser.role) {
      showSnackbar('Admin context missing. Please login again.', 'error');
      return;
    }

    setIsResettingPassword(true);
    try {
      await axiosInstance.post(
        `/api/students/${studentId}/reset-password-admin`,
        {},
        {
          headers: {
            userId: currentUser.userId,
            schoolId: currentUser.schoolId,
            role: currentUser.role,
          },
        }
      );
      showSnackbar('Student password reset successful.');
    } catch (error) {
      handleError(error, 'Failed to reset password.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const canResetPassword = ['SCHOOL_ADMIN', 'ADMIN', 'school_admin', 'admin'].includes(
    String(currentUser.role || '')
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!student) {
    return (
      <PageContainer>
        <EmptyState
          title="Student details unavailable"
          description="The requested student could not be loaded."
          action={
            <Button variant="outlined" onClick={() => navigate('/students/list')}>
              Back to Students
            </Button>
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={`${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student'}
        subtitle={`Admission No: ${student.admissionNumber || 'N/A'} | Class: ${
          student.classes?.className || 'N/A'
        } ${student.section?.sectionName ? `- ${student.section.sectionName}` : ''}`}
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" />}
              disabled={uploading}
            >
              Upload Photo
              <input hidden type="file" accept="image/jpeg,image/png,image/gif" onChange={handleImageUpload} />
            </Button>
            {student.imageUrl ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={handleClearImage}
                disabled={uploading}
              >
                Remove Photo
              </Button>
            ) : null}
            {canResetPassword ? (
              <Button
                variant="outlined"
                color="warning"
                startIcon={isResettingPassword ? <CircularProgress size={14} /> : <LockResetIcon fontSize="small" />}
                onClick={() => setOpenResetConfirmDialog(true)}
                disabled={isResettingPassword}
              >
                Reset Password
              </Button>
            ) : null}
            <Button
              variant="contained"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => navigate(`/student/edit/${studentId}`)}
            >
              Edit
            </Button>
          </Stack>
        }
      />

      <Paper
        variant="outlined"
        sx={{
          p: 2.25,
          borderRadius: 1.5,
          background: 'linear-gradient(135deg, rgba(245,248,255,0.98) 0%, rgba(255,255,255,0.98) 100%)',
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Avatar
                src={student.imageUrl || ''}
                sx={{ width: 116, height: 116, borderRadius: 4, bgcolor: 'rgba(59, 130, 246, 0.10)', color: 'primary.main' }}
              >
                {!student.imageUrl ? <PersonIcon fontSize="large" /> : null}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {`${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  A simple overview with only the student details, fee actions, and latest academic updates you usually need.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.4 }}>
                  {[
                    `Admission ${student.admissionNumber || 'N/A'}`,
                    `${student.classes?.className || 'Class N/A'}${student.section?.sectionName ? ` / ${student.section.sectionName}` : ''}`,
                    student.parentName || 'Parent N/A',
                    student.parentPhone || student.phone || 'Phone N/A',
                  ].map((label) => (
                    <Chip key={label} label={label} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.92)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Student Fee
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Refreshed {refreshedAtLabel}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {[
                  ['Assigned Fee', 'Open student fee page'],
                  ['Add Payment', 'Record a new payment quickly'],
                  ['Receipts', 'View history and print receipts'],
                ].map(([label, value]) => (
                  <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.75 }}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  onClick={() => navigate(`/students/${studentId}/assign-fee`)}
                >
                  Open Student Fee
                </Button>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/fees/create?studentId=${studentId}`)}
                >
                  Add Payment
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              {basicRows.map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(15, 23, 42, 0.03)', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.35 }}>
                      {value || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Paper variant="outlined" sx={{ p: 1.8 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>
                Quick Summary
              </Typography>
              <Grid container spacing={1.2}>
                {quickStats.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(15, 23, 42, 0.03)', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.35 }}>
                        {item.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.helper}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Contact & Support
            </Typography>
            <Grid container spacing={1.5}>
              {[...contactRows, ...supportRows].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: 'rgba(15, 23, 42, 0.03)', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.35 }}>
                      {value || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Latest Exam Results
            </Typography>
            {loadingExamResults ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : subjectResults.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjectResults.slice(0, 6).map((subject, index) => {
                    const percentage = Number.isFinite(subject?.percentage)
                      ? `${Number(subject.percentage).toFixed(1)}%`
                      : (Number.isFinite(subject?.marksObtained) && Number.isFinite(subject?.maxMarks) && subject.maxMarks > 0)
                        ? `${((subject.marksObtained / subject.maxMarks) * 100).toFixed(1)}%`
                        : 'N/A';
                    return (
                      <TableRow key={`${subject?.subjectName || 'subject'}-${index}`}>
                        <TableCell sx={{ fontWeight: 700 }}>{subject?.subjectName || 'Subject'}</TableCell>
                        <TableCell>{Number.isFinite(subject?.marksObtained) ? `${subject.marksObtained}${Number.isFinite(subject?.maxMarks) ? ` / ${subject.maxMarks}` : ''}` : 'N/A'}</TableCell>
                        <TableCell>{percentage}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No exam results available yet for this student.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Awards & Certificates
        </Typography>
        {loadingAchievements ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : achievements.length > 0 ? (
          <Stack spacing={1.1}>
            {achievements.slice(0, 5).map((achievement) => (
              <Box
                key={achievement.id}
                sx={{
                  p: 1.3,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(15, 23, 42, 0.03)',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {achievement.title || 'Achievement'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                  {achievement.description || 'No description provided.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Awarded on {formatDate(achievement.dateAwarded)}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No awards or certificates recorded yet.
          </Typography>
        )}
      </Paper>

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate('/students/list')}>
          Back
        </Button>
      </Stack>

      <Dialog open={openResetConfirmDialog} onClose={() => setOpenResetConfirmDialog(false)}>
        <DialogTitle>Reset Student Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm password reset for {student.firstName} {student.lastName}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetConfirmDialog(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleConfirmResetPassword}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default ViewStudent;
